/**
 * DataSyncEngine Service - Zustand Store
 * Single Responsibility: Coordinate syncing between master and child
 */

import { create } from 'zustand';
import { usePathNavigatorStore } from './PathNavigatorStore';
import { useChangeDetectorStore } from './ChangeDetectorStore';
import { useErrorHandlerStore } from './ErrorHandlerStore';
import type { DataBindingConfig, IBindingStrategy, DataBindingError } from '../types/index';

export interface DataSyncEngineStore {
  /**
   * Sync child changes to master data
   */
  sync: (
    master: any,
    child: any,
    binding: DataBindingConfig,
    strategy: IBindingStrategy
  ) => void;

  /**
   * Rollback sync on error
   */
  rollback: (master: any, previous: any, binding: DataBindingConfig) => void;

  /**
   * Create error object
   */
  createError: (code: string, message: string, context?: any) => DataBindingError;
}

export const useDataSyncEngineStore = create<DataSyncEngineStore>(
  (set, get) => ({
    sync: (
      master: any,
      child: any,
      binding: DataBindingConfig,
      strategy: IBindingStrategy
    ): void => {
      try {
        const pathNavigator = usePathNavigatorStore.getState();
        const changeDetector = useChangeDetectorStore.getState();
        const errorHandler = useErrorHandlerStore.getState();

        const initialData = strategy.getInitialData(master, binding);
        const changes = changeDetector.getChangeSet(initialData, child);

        if (Object.keys(changes).length === 0) {
          return; // No changes
        }

        strategy.syncToMaster(master, child, binding);
      } catch (error) {
        const errorHandler = useErrorHandlerStore.getState();
        const syncError: DataBindingError = {
          code: 'SYNC_FAILED',
          message: `Failed to sync data: ${(error as Error).message}`,
          context: { error, binding },
        };
        errorHandler.handle(syncError);
        throw syncError;
      }
    },

    rollback: (master: any, previous: any, binding: DataBindingConfig): void => {
      try {
        const pathNavigator = usePathNavigatorStore.getState();

        if (binding.mode === 'sub-root' && 'rootPath' in binding) {
          pathNavigator.setValue(master, binding.rootPath, previous);
        } else if (binding.mode === 'shared-root') {
          Object.assign(master, previous);
        }
      } catch (error) {
        const errorHandler = useErrorHandlerStore.getState();
        const rollbackError: DataBindingError = {
          code: 'SYNC_FAILED',
          message: `Failed to rollback: ${(error as Error).message}`,
          context: { error, binding },
        };
        errorHandler.handle(rollbackError);
      }
    },

    createError: (
      code: string,
      message: string,
      context?: any
    ): DataBindingError => {
      return {
        code: code as DataBindingError['code'],
        message,
        context,
      };
    },
  })
);
